import { Box, CircularProgress, Grid2, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import QRCode from 'react-qr-code'
import { useNavigate } from 'react-router-dom'

import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import VerificationInput from 'components/VerificationInput'
import { dashboardPath } from 'components/Routes'
import axios from 'utils/axios'
import { getTokens } from 'utils/jwtStorage'
import theme from 'utils/theme'

// Shape AFTER the axios envelope interceptor unwraps { status, data } -> data.
interface AssociateSoftwareTokenResponse {
  secretCode: string
  session: string | null
}

interface VerifySoftwareTokenResponse {
  status: string
}

export default function Reenrol(): JSX.Element {
  const { setToast } = useAuth()
  const navigate = useNavigate()

  const [secretCode, setSecretCode] = useState<string>('')
  const [qrLoading, setQrLoading] = useState<boolean>(true)
  const [qrError, setQrError] = useState<string>('')

  const [code, setCode] = useState<string>('')
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<string>('')

  useEffect(() => {
    async function associateToken() {
      try {
        const tokens = getTokens()
        const response = await axios.post<AssociateSoftwareTokenResponse>(
          '/auth/associate-software-token',
          { accessToken: tokens?.accessToken }
        )
        setSecretCode(response.data.secretCode)
      } catch {
        setQrError('Failed to load QR code. Please refresh and try again.')
      } finally {
        setQrLoading(false)
      }
    }

    void associateToken()
  }, [])

  function handleCodeChange(value: string) {
    setSubmitError('')
    setCode(value)
  }

  async function handleSubmit() {
    if (!code || code.length < 6) return
    setSubmitLoading(true)
    setSubmitError('')

    try {
      const tokens = getTokens()
      const response = await axios.post<VerifySoftwareTokenResponse>(
        '/auth/verify-software-token',
        { accessToken: tokens?.accessToken, userCode: code }
      )

      if (response.data.status === 'SUCCESS') {
        setToast({
          open: true,
          message: 'Two-factor authentication re-enrolled successfully',
          type: 'success',
          duration: 3000
        })
        navigate(dashboardPath())
      } else {
        setSubmitError('Verification failed. Please try again.')
      }
    } catch {
      setSubmitError(
        'Invalid code. Please check your authenticator app and try again.'
      )
    } finally {
      setSubmitLoading(false)
    }
  }

  const issuerName = 'Goldie'
  const otpAuthUri = secretCode
    ? `otpauth://totp/${issuerName}?secret=${secretCode}&issuer=${issuerName}`
    : ''

  return (
    <Grid2 container sx={{ maxWidth: 480, mx: 'auto', px: 2, py: 4 }}>
      <Grid2 size={{ xs: 12 }}>
        <Typography
          align='left'
          variant='h2'
          fontSize='48px'
          textTransform='uppercase'
          className='font-ivypresto'
        >
          Re-enrol authenticator
        </Typography>
      </Grid2>

      <Grid2 mb={4} size={{ xs: 12 }}>
        <Typography align='left' fontSize='medium'>
          Your previous two-factor authentication setup needs to be updated.
          Scan this QR code with your authenticator app (Google Authenticator,
          Authy, etc.) to re-enrol.
        </Typography>
      </Grid2>

      <Grid2
        size={{ xs: 12 }}
        sx={{
          backgroundColor: '#ffc2f8',
          borderRadius: 2,
          p: 3,
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        {qrLoading && (
          <Box
            sx={{
              width: 256,
              height: 256,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {qrError && !qrLoading && (
          <Box sx={{ color: theme.palette.error.main, textAlign: 'center' }}>
            <Typography>{qrError}</Typography>
          </Box>
        )}

        {!qrLoading && !qrError && secretCode && (
          <>
            <QRCode id='reenrolQrCode' value={otpAuthUri} size={256} />
            <Typography
              mt={2}
              fontSize='small'
              sx={{ wordBreak: 'break-all', textAlign: 'center' }}
              className='font-antonio'
            >
              MANUAL CODE: {secretCode}
            </Typography>
          </>
        )}
      </Grid2>

      <Grid2 sx={{ marginTop: 6 }} size={{ xs: 12 }}>
        <Typography mb={1} className='font-antonio' textTransform='uppercase'>
          Enter the 6-digit code from your app
        </Typography>
        <VerificationInput onChange={handleCodeChange} />
      </Grid2>

      <Grid2 sx={{ textAlign: 'center', mb: 2 }} size={{ xs: 12 }}>
        {submitError && (
          <Box
            sx={{
              color: theme.palette.error.main,
              fontSize: '16px',
              mt: 2
            }}
          >
            {submitError}
          </Box>
        )}
      </Grid2>

      <Grid2 sx={{ mt: 4, textAlign: 'center' }} size={{ xs: 12 }}>
        <Button
          color='primary'
          fullWidth
          onClick={handleSubmit}
          disabled={submitLoading || qrLoading || code.length < 6}
          sx={{
            backgroundColor: '#ff7bac',
            '&:hover': { backgroundColor: '#ff7bac90' },
            color: 'black'
          }}
        >
          {submitLoading ? 'Verifying…' : 'Confirm re-enrolment'}
        </Button>
      </Grid2>
    </Grid2>
  )
}
